import React, { useState, useEffect, useContext, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";
import Box from "@material-ui/core/Box";
import RefreshIcon from "@material-ui/icons/Refresh";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import WarningIcon from "@material-ui/icons/Warning";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ChatBubbleIcon from "@material-ui/icons/ChatBubble";
import PersonIcon from "@material-ui/icons/Person";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ForbiddenPage from "../../components/ForbiddenPage";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getBackendUrl } from "../../config";
import moment from "moment";
import "moment/locale/pt-br";

moment.locale("pt-br");

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    marginTop: theme.spacing(1),
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
      : "linear-gradient(135deg, #f8f9ff 0%, #eef1ff 100%)",
    borderRadius: 16,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(66,84,203,0.12)"}`,
  },
  refreshBtn: {
    color: theme.palette.type === "dark" ? "#7c8cf8" : "#4254cb",
    transition: "transform 0.3s",
    "&:hover": { transform: "rotate(180deg)" },
  },
  lastUpdate: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
  },
  tableHead: {
    background: theme.palette.type === "dark"
      ? "rgba(66,84,203,0.25)"
      : "rgba(66,84,203,0.08)",
  },
  tableHeadCell: {
    fontWeight: 700,
    fontSize: 12,
    color: theme.palette.type === "dark" ? "#a5b4fc" : "#4254cb",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    borderBottom: `2px solid ${theme.palette.type === "dark" ? "rgba(66,84,203,0.4)" : "rgba(66,84,203,0.2)"}`,
    whiteSpace: "nowrap",
  },
  tableRow: {
    transition: "background 0.15s",
    "&:hover": {
      background: theme.palette.type === "dark"
        ? "rgba(66,84,203,0.12)"
        : "rgba(66,84,203,0.05)",
    },
  },
  tableCell: {
    fontSize: 13,
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
  },
  avatar: {
    width: 36,
    height: 36,
    fontSize: 14,
    fontWeight: 700,
    background: "linear-gradient(135deg, #4254cb 0%, #7c8cf8 100%)",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  userName: {
    fontWeight: 600,
    fontSize: 13,
  },
  userEmail: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  // Status badges
  statusOnline: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 10px",
    borderRadius: 20,
    background: "rgba(6,194,112,0.12)",
    color: "#06c270",
    fontWeight: 600,
    fontSize: 11,
    border: "1px solid rgba(6,194,112,0.3)",
  },
  statusIdle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 10px",
    borderRadius: 20,
    background: "rgba(255,152,0,0.12)",
    color: "#ff9800",
    fontWeight: 600,
    fontSize: 11,
    border: "1px solid rgba(255,152,0,0.3)",
  },
  statusOffline: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 10px",
    borderRadius: 20,
    background: "rgba(158,158,158,0.1)",
    color: "#9e9e9e",
    fontWeight: 600,
    fontSize: 11,
    border: "1px solid rgba(158,158,158,0.2)",
  },
  // Ticket count chips
  chipOpen: {
    background: "rgba(3,169,244,0.12)",
    color: "#0288d1",
    fontWeight: 700,
    fontSize: 12,
    height: 24,
    border: "1px solid rgba(3,169,244,0.3)",
  },
  chipPending: {
    background: "rgba(255,193,7,0.12)",
    color: "#f57f17",
    fontWeight: 700,
    fontSize: 12,
    height: 24,
    border: "1px solid rgba(255,193,7,0.3)",
  },
  chipClosed: {
    background: "rgba(6,194,112,0.12)",
    color: "#06c270",
    fontWeight: 700,
    fontSize: 12,
    height: 24,
    border: "1px solid rgba(6,194,112,0.3)",
  },
  chipStale: {
    background: "rgba(229,57,53,0.12)",
    color: "#e53935",
    fontWeight: 700,
    fontSize: 12,
    height: 24,
    border: "1px solid rgba(229,57,53,0.3)",
    animation: "$pulseStale 2s infinite",
  },
  "@keyframes pulseStale": {
    "0%": { boxShadow: "0 0 0 0 rgba(229,57,53,0.4)" },
    "70%": { boxShadow: "0 0 0 6px rgba(229,57,53,0)" },
    "100%": { boxShadow: "0 0 0 0 rgba(229,57,53,0)" },
  },
  lastMsgTime: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  noData: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  summaryBar: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  summaryCard: {
    flex: "1 1 120px",
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 120,
  },
  summaryOnline: {
    background: "rgba(6,194,112,0.08)",
    border: "1px solid rgba(6,194,112,0.2)",
  },
  summaryIdle: {
    background: "rgba(255,152,0,0.08)",
    border: "1px solid rgba(255,152,0,0.2)",
  },
  summaryOffline: {
    background: "rgba(158,158,158,0.06)",
    border: "1px solid rgba(158,158,158,0.15)",
  },
  summaryStale: {
    background: "rgba(229,57,53,0.08)",
    border: "1px solid rgba(229,57,53,0.2)",
  },
  summaryNum: {
    fontSize: 26,
    fontWeight: 800,
    lineHeight: 1,
  },
  summaryLabel: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
}));

const StatusBadge = ({ status, classes }) => {
  if (status === "online") {
    return (
      <span className={classes.statusOnline}>
        <FiberManualRecordIcon style={{ fontSize: 8 }} />
        Online
      </span>
    );
  }
  if (status === "idle") {
    return (
      <span className={classes.statusIdle}>
        <AccessTimeIcon style={{ fontSize: 11 }} />
        Ocioso
      </span>
    );
  }
  return (
    <span className={classes.statusOffline}>
      <FiberManualRecordIcon style={{ fontSize: 8 }} />
      Offline
    </span>
  );
};

const UserProductivity = () => {
  const classes = useStyles();
  const { user: loggedInUser } = useContext(AuthContext);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/dashboard/user-productivity");
      setData(res.data || []);
      setLastUpdate(new Date());
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (loggedInUser.profile !== "admin") return;
    fetchData();
    // Auto-refresh a cada 60s
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData, loggedInUser.profile]);

  if (loggedInUser.profile !== "admin") {
    return <ForbiddenPage />;
  }

  const online = data.filter((u) => u.status_atual === "online").length;
  const idle = data.filter((u) => u.status_atual === "idle").length;
  const offline = data.filter((u) => u.status_atual === "offline").length;
  const totalStale = data.reduce((acc, u) => acc + Number(u.tickets_sem_resposta || 0), 0);

  return (
    <MainContainer>
      <MainHeader>
        <Title>
          Produtividade dos Atendentes
        </Title>
        <MainHeaderButtonsWrapper>
          {lastUpdate && (
            <span className={classes.lastUpdate}>
              Atualizado {moment(lastUpdate).fromNow()}
            </span>
          )}
          <Tooltip title="Atualizar agora">
            <IconButton
              className={classes.refreshBtn}
              onClick={fetchData}
              disabled={loading}
              id="btn-refresh-productivity"
            >
              {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {/* Summary Bar */}
        <div className={classes.summaryBar}>
          <div className={`${classes.summaryCard} ${classes.summaryOnline}`}>
            <CheckCircleIcon style={{ color: "#06c270", fontSize: 28 }} />
            <div>
              <div className={classes.summaryNum} style={{ color: "#06c270" }}>{online}</div>
              <div className={classes.summaryLabel}>Online Ativo</div>
            </div>
          </div>
          <div className={`${classes.summaryCard} ${classes.summaryIdle}`}>
            <AccessTimeIcon style={{ color: "#ff9800", fontSize: 28 }} />
            <div>
              <div className={classes.summaryNum} style={{ color: "#ff9800" }}>{idle}</div>
              <div className={classes.summaryLabel}>Ocioso / Ausente</div>
            </div>
          </div>
          <div className={`${classes.summaryCard} ${classes.summaryOffline}`}>
            <PersonIcon style={{ color: "#9e9e9e", fontSize: 28 }} />
            <div>
              <div className={classes.summaryNum} style={{ color: "#9e9e9e" }}>{offline}</div>
              <div className={classes.summaryLabel}>Offline</div>
            </div>
          </div>
          <div className={`${classes.summaryCard} ${classes.summaryStale}`}>
            <WarningIcon style={{ color: "#e53935", fontSize: 28 }} />
            <div>
              <div className={classes.summaryNum} style={{ color: "#e53935" }}>{totalStale}</div>
              <div className={classes.summaryLabel}>Sem Resposta &gt;30min</div>
            </div>
          </div>
        </div>

        {/* Table */}
        <Table size="small" id="table-user-productivity">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.tableHeadCell}>Atendente</TableCell>
              <TableCell className={classes.tableHeadCell} align="center">Status</TableCell>
              <TableCell className={classes.tableHeadCell} align="center">Última Atividade</TableCell>
              <TableCell className={classes.tableHeadCell} align="center">
                <Tooltip title="Em atendimento (abertos)"><span>Abertos</span></Tooltip>
              </TableCell>
              <TableCell className={classes.tableHeadCell} align="center">
                <Tooltip title="Aguardando cliente (pendentes)"><span>Aguardando</span></Tooltip>
              </TableCell>
              <TableCell className={classes.tableHeadCell} align="center">
                <Tooltip title="Fechados hoje"><span>Fechados Hoje</span></Tooltip>
              </TableCell>
              <TableCell className={classes.tableHeadCell} align="center">
                <Tooltip title="Tickets aguardando resposta do atendente há mais de 30 min">
                  <span style={{ color: "#e53935" }}>Sem Resposta</span>
                </Tooltip>
              </TableCell>
              <TableCell className={classes.tableHeadCell} align="center">Última Mensagem Enviada</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" style={{ padding: 40 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <div className={classes.noData}>
                    <PersonIcon style={{ fontSize: 48, opacity: 0.3 }} />
                    <div>Nenhum atendente encontrado</div>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              data.map((u) => {
                const initials = (u.name || "?")
                  .split(" ")
                  .slice(0, 2)
                  .map((w) => w[0])
                  .join("")
                  .toUpperCase();

                const activityTime = u.lastActivityAt || u.lastSeen;
                const lastMsgAt = u.last_msg_at;
                const stale = Number(u.tickets_sem_resposta || 0);

                return (
                  <TableRow key={u.id} className={classes.tableRow}>
                    {/* Atendente */}
                    <TableCell className={classes.tableCell}>
                      <div className={classes.userInfo}>
                        <Avatar
                          className={classes.avatar}
                          src={u.avatar ? `${getBackendUrl()}/public/${u.avatar}` : undefined}
                        >
                          {initials}
                        </Avatar>
                        <div>
                          <div className={classes.userName}>{u.name}</div>
                          <div className={classes.userEmail}>{u.email}</div>
                        </div>
                      </div>
                    </TableCell>

                    {/* Status */}
                    <TableCell className={classes.tableCell} align="center">
                      <StatusBadge status={u.status_atual} classes={classes} />
                    </TableCell>

                    {/* Última Atividade */}
                    <TableCell className={classes.tableCell} align="center">
                      {activityTime ? (
                        <Tooltip title={moment(activityTime).format("DD/MM/YYYY HH:mm:ss")}>
                          <span className={classes.lastMsgTime}>
                            <AccessTimeIcon style={{ fontSize: 13, opacity: 0.6 }} />
                            {moment(activityTime).fromNow()}
                          </span>
                        </Tooltip>
                      ) : (
                        <span style={{ color: "#bdbdbd", fontSize: 12 }}>—</span>
                      )}
                    </TableCell>

                    {/* Abertos */}
                    <TableCell className={classes.tableCell} align="center">
                      <Chip
                        label={u.tickets_abertos}
                        size="small"
                        className={classes.chipOpen}
                      />
                    </TableCell>

                    {/* Aguardando */}
                    <TableCell className={classes.tableCell} align="center">
                      <Chip
                        label={u.tickets_aguardando}
                        size="small"
                        className={classes.chipPending}
                      />
                    </TableCell>

                    {/* Fechados Hoje */}
                    <TableCell className={classes.tableCell} align="center">
                      <Chip
                        label={u.tickets_fechados_hoje}
                        size="small"
                        className={classes.chipClosed}
                      />
                    </TableCell>

                    {/* Sem Resposta */}
                    <TableCell className={classes.tableCell} align="center">
                      {stale > 0 ? (
                        <Tooltip title={`${stale} ticket(s) aguardando resposta há mais de 30 minutos`}>
                          <Chip
                            icon={<WarningIcon style={{ fontSize: 14 }} />}
                            label={stale}
                            size="small"
                            className={classes.chipStale}
                          />
                        </Tooltip>
                      ) : (
                        <CheckCircleIcon style={{ fontSize: 18, color: "#06c270", opacity: 0.7 }} />
                      )}
                    </TableCell>

                    {/* Última mensagem enviada */}
                    <TableCell className={classes.tableCell} align="center">
                      {lastMsgAt ? (
                        <Tooltip title={`"${u.last_msg_body ? u.last_msg_body.substring(0, 80) : "..."}" — ${moment(lastMsgAt).format("DD/MM HH:mm")}`}>
                          <span className={classes.lastMsgTime} style={{ justifyContent: "center" }}>
                            <ChatBubbleIcon style={{ fontSize: 13, opacity: 0.6 }} />
                            {moment(lastMsgAt).fromNow()}
                          </span>
                        </Tooltip>
                      ) : (
                        <span style={{ color: "#bdbdbd", fontSize: 12 }}>—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default UserProductivity;
